import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Loader2, LogIn, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && typeof user === "object") return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) navigate("/");
    else setError(r.error || "Login failed");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white grid lg:grid-cols-2">
      {/* Left side — brand panel */}
      <div className="relative hidden lg:block border-r border-zinc-900 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 bg-emerald-500 grid place-items-center">
              <span className="font-display font-bold text-black text-sm">I</span>
            </div>
            <div className="font-display font-bold tracking-[0.18em] text-sm">ISF OPS</div>
          </div>

          <div>
            <div className="overline mb-4">Internal control room</div>
            <h1 className="font-display font-bold text-5xl xl:text-6xl leading-[0.95] tracking-tight">
              India&rsquo;s biggest<br />
              <span className="text-emerald-500">sneaker festival,</span><br />
              run from one screen.
            </h1>
            <p className="mt-6 text-zinc-400 max-w-md">
              Sponsorship pipeline, ₹16.65 Cr inventory, BookMyShow telemetry and
              creator reach — tracked in real-time for Delhi · Dec 19–20, 2026.
            </p>
          </div>

          <div className="flex items-center justify-between text-xs text-zinc-500 tabular">
            <div>
              <div className="overline mb-1">Edition</div>
              <div className="font-display text-white text-base">Delhi 2026</div>
            </div>
            <div>
              <div className="overline mb-1">Dates</div>
              <div className="font-display text-white text-base">19 – 20 Dec</div>
            </div>
            <div>
              <div className="overline mb-1">Inventory</div>
              <div className="font-display text-white text-base">₹ 16.65 Cr</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side — form */}
      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="overline mb-3">Sign in</div>
          <h2 className="font-display font-bold text-3xl tracking-tight mb-1">Welcome back.</h2>
          <p className="text-zinc-400 text-sm mb-8">Enter your credentials to access ISF Ops.</p>

          <label className="block text-xs uppercase tracking-[0.18em] text-zinc-500 mb-2">Email</label>
          <input
            data-testid="email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@indiansneakerfestival.com"
            className="w-full bg-[#111111] border border-zinc-800 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 transition-colors mb-5"
          />

          <label className="block text-xs uppercase tracking-[0.18em] text-zinc-500 mb-2">Password</label>
          <input
            data-testid="password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="w-full bg-[#111111] border border-zinc-800 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 transition-colors mb-6"
          />

          {error && (
            <div data-testid="login-error" className="text-sm text-red-400 mb-4 border border-red-500/30 bg-red-500/10 px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-medium py-3 transition-colors"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
            <span>Sign in</span>
            <ArrowRight className="h-4 w-4 ml-1" />
          </button>

          <div className="mt-10 pt-6 border-t border-zinc-900 text-xs text-zinc-500 leading-relaxed">
            Authorised access only · Indian Sneaker Festival ops
          </div>
        </form>
      </div>
    </div>
  );
}
